import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import {
    gsap,
    MOTION_QUERIES,
    primeOnRefresh,
    useGSAP
} from '@/lib/motion'
import SectionHeading from '@/components/landing/section-heading'
import ContactForm from '@/components/landing/contact-form'
import Sparkle from '@/components/landing/decor/sparkle'
import MahoniaSprig from '@/components/landing/decor/mahonia'
import NightSky from '@/components/landing/decor/night-sky'
import { CONTACT_COPY } from '@/constants/content'

const round = (value: number): number => Math.round(value * 100) / 100

const ORBIT_STARS = Array.from({ length: 14 }, (_, index) => {
    const ring = index % 2 === 0 ? 44 : 33
    const angle = ((index / 14) * 360 + (index % 2 ? 12 : 0)) * (Math.PI / 180)

    return {
        x: round(50 + Math.cos(angle) * ring),
        y: round(50 + Math.sin(angle) * ring),
        size: index % 3 === 0 ? 16 : index % 3 === 1 ? 10 : 7,
        gold: index % 4 !== 1,
        fromX: (((index * 37) % 100) - 50) / 50,
        fromY: (((index * 53) % 100) - 50) / 50
    }
})

// CONTACT: the motion settles. The stars that drifted through the page gather quietly around one
// large ✦, the plants stand still, and the observation ends with a message form.
const ContactSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)

    useGSAP(() => {
        const section = sectionRef.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        mm.add({ desktop: MOTION_QUERIES.desktop, mobile: MOTION_QUERIES.mobile }, context => {
            const isDesktop = Boolean(context.conditions?.desktop)
            const gather = gsap.timeline({
                scrollTrigger: { trigger: section, start: 'top 85%', end: 'top 5%', scrub: 1.2, invalidateOnRefresh: true, onRefresh: primeOnRefresh }
            })

            const orbitStars = q('[data-orbit-star]')
            const span = 0.5 + Math.max(0, orbitStars.length - 1) * 0.02

            // One tween per star (no `stagger`), so a refresh keeps every star at its starting point.
            orbitStars.forEach((star, index) => {
                gather.fromTo(star, {
                    x: () => ORBIT_STARS[index].fromX * window.innerWidth * 0.42,
                    y: () => ORBIT_STARS[index].fromY * window.innerHeight * 0.4,
                    autoAlpha: 0.2,
                    scale: 0.6
                }, {
                    x: 0,
                    y: 0,
                    autoAlpha: 1,
                    scale: 1,
                    ease: 'power2.out',
                    duration: 0.5
                }, index * 0.02)
            })

            gather
                .fromTo(q('[data-contact="big-star"]'), { scale: 0.4, autoAlpha: 0, rotate: -45 }, { scale: 1, autoAlpha: 1, rotate: 0, ease: 'power2.out', duration: span }, 0)
                .fromTo(q('[data-contact="orbits"]'), { autoAlpha: 0, scale: 0.85 }, { autoAlpha: 1, scale: 1, ease: 'power2.out', duration: span }, 0)

            gsap.timeline({ scrollTrigger: { trigger: q('[data-contact="text"]')[0], start: 'top 80%' } })
                .from(q('[data-heading-meta]'), { autoAlpha: 0, y: 12, duration: 1.2, stagger: 0.12, ease: 'power2.out' })
                .from(q('[data-char]'), { autoAlpha: 0, y: 36, filter: isDesktop ? 'blur(8px)' : 'blur(0px)', duration: 1.6, stagger: 0.09, ease: 'power3.out' }, 0)
                .from(q('[data-contact-fade]'), { autoAlpha: 0, y: 24, duration: 1.4, stagger: 0.18, ease: 'power2.out' }, 0.5)
        })
    }, { scope: sectionRef })

    return (
        <section id='contact' ref={sectionRef} className='relative overflow-hidden bg-night text-mist'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                <div className='absolute inset-x-0 top-0 h-[120vh]'>
                    <NightSky className='absolute inset-0 h-full w-full' />
                </div>
                <div className='absolute inset-0 bg-[linear-gradient(180deg,rgba(20,27,46,0)_0%,rgba(20,27,46,0.35)_55vh,#141b2e_115vh)]' />
                <div className='absolute inset-x-0 top-0 h-[38vh] bg-[linear-gradient(180deg,#dcd8e4_0%,rgba(141,143,176,0.75)_35%,rgba(58,68,102,0.35)_70%,rgba(20,27,46,0)_100%)]' />
            </div>

            <div aria-hidden='true' className='pointer-events-none absolute bottom-0 left-[-14vw] w-[48vw] max-w-[440px] opacity-90 lg:left-[-3vw] lg:w-[20vw]'>
                <MahoniaSprig seed={81} tone='night' />
            </div>
            <div aria-hidden='true' className='pointer-events-none absolute bottom-0 right-[-16vw] w-[46vw] max-w-[420px] opacity-90 lg:right-[-4vw] lg:w-[18vw]'>
                <MahoniaSprig seed={87} tone='night' className='-scale-x-100' />
            </div>

            <div className='relative mx-auto flex max-w-5xl flex-col items-center px-5 pb-24 pt-[24vh] lg:pb-32 lg:pt-[30vh]'>
                <div aria-hidden='true' className='relative size-[min(76vw,420px)]'>
                    <svg data-contact='orbits' viewBox='0 0 100 100' fill='none' className='absolute inset-0 h-full w-full'>
                        <circle cx='50' cy='50' r='44' stroke='rgba(251,250,246,0.16)' strokeWidth='0.2' />
                        <circle cx='50' cy='50' r='33' stroke='rgba(251,250,246,0.22)' strokeWidth='0.2' strokeDasharray='0.6 1.6' />
                        <circle cx='50' cy='50' r='20' stroke='rgba(226,179,60,0.25)' strokeWidth='0.2' />
                    </svg>

                    <div className='absolute inset-0 motion-safe:animate-spin-slow'>
                        {ORBIT_STARS.map((star, index) => (
                            <span
                                key={index}
                                className='absolute block'
                                style={{ left: `${star.x}%`, top: `${star.y}%` }}
                            >
                                <span className='block -translate-x-1/2 -translate-y-1/2'>
                                    <span data-orbit-star className='block'>
                                        <Sparkle
                                            className={cn(
                                                'block motion-safe:animate-twinkle',
                                                star.gold
                                                    ? 'text-gold drop-shadow-[0_0_6px_rgba(255,226,150,0.85)]'
                                                    : 'text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.9)]'
                                            )}
                                            style={{ width: star.size, height: star.size, animationDelay: `${(index % 5) * 0.7}s` }}
                                        />
                                    </span>
                                </span>
                            </span>
                        ))}
                    </div>

                    <div className='absolute inset-0 flex items-center justify-center'>
                        <div data-contact='big-star' className='relative flex size-[30%] items-center justify-center'>
                            <span className='absolute inset-[-90%] rounded-full bg-[radial-gradient(closest-side,rgba(255,226,150,0.42),rgba(255,226,150,0.08)_60%,rgba(255,226,150,0))]' />
                            <Sparkle className='relative h-full w-full text-gold drop-shadow-[0_0_30px_rgba(255,226,150,0.85)]' />
                        </div>
                    </div>
                </div>

                <div data-contact='text' data-scroll-anchor className='mt-12 flex w-full scroll-mt-[76px] flex-col items-center text-center lg:scroll-mt-[96px]'>
                    <SectionHeading
                        index={CONTACT_COPY.index}
                        kicker={CONTACT_COPY.kicker}
                        title={CONTACT_COPY.title}
                        ja={CONTACT_COPY.ja}
                        tone='dark'
                        className='items-center'
                        titleClassName='text-[clamp(2.75rem,9vw,4.25rem)] lg:text-[clamp(3.5rem,5.5vw,5.75rem)]'
                    />
                    <div data-contact-fade className='mt-10 flex max-w-xl flex-col gap-5 text-[15px] leading-[2.05] tracking-[0.05em] text-mist/80'>
                        {CONTACT_COPY.body.map(lines => (
                            <p key={lines[0]}>
                                {lines.map((line, index) => (
                                    <span key={line}>
                                        {index > 0 && <br className='hidden sm:inline' />}
                                        {line}
                                    </span>
                                ))}
                            </p>
                        ))}

                        <div className='mx-auto flex max-w-lg flex-col gap-3 border-t border-white/10 pt-5 text-xs leading-[2] tracking-[0.04em] text-mist/60'>
                            {CONTACT_COPY.note.map(lines => (
                                <p key={lines[0]}>
                                    {lines.map((line, index) => (
                                        <span key={line}>
                                            {index > 0 && <br className='hidden sm:inline' />}
                                            {line}
                                        </span>
                                    ))}
                                </p>
                            ))}
                        </div>
                    </div>

                    <div data-contact-fade className='mt-14 w-full max-w-2xl rounded-[24px] bg-white/[0.06] p-6 text-left ring-1 ring-white/15 backdrop-blur-md sm:p-10'>
                        <ContactForm />
                    </div>
                </div>
            </div>
        </section>
    )
}

export default ContactSection
